import type {
  EquipmentInstance,
  EquipmentPoint,
  EquipmentPort,
  EquipmentProperties,
  EquipmentStyle,
  EquipmentTemplate,
  EquipmentReviewStatus,
} from '../features/equipment/equipmentTypes';
import type { DxfLayer, LayoutElement } from '../types';
import { defaultStyleForType } from '../features/equipment/equipmentConstants';
import { makeDefaultPorts, normalizePorts } from './ports';

export type Bounds = { minX: number; minY: number; maxX: number; maxY: number };

/** Axis-aligned bounding box of a polygon (pre-rotation). */
export function polygonBounds(polygon: EquipmentPoint[]): Bounds {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const p of polygon) {
    if (p.x < minX) minX = p.x;
    if (p.y < minY) minY = p.y;
    if (p.x > maxX) maxX = p.x;
    if (p.y > maxY) maxY = p.y;
  }
  if (!Number.isFinite(minX)) return { minX: 0, minY: 0, maxX: 0, maxY: 0 };
  return { minX, minY, maxX, maxY };
}

/** Centroid used as the rotation pivot (vertex average — stable for footprints). */
export function polygonCentroid(polygon: EquipmentPoint[]): EquipmentPoint {
  if (polygon.length === 0) return { x: 0, y: 0 };
  let sx = 0;
  let sy = 0;
  for (const p of polygon) {
    sx += p.x;
    sy += p.y;
  }
  return { x: sx / polygon.length, y: sy / polygon.length };
}

export function rotatePoint(
  p: EquipmentPoint,
  pivot: EquipmentPoint,
  degrees: number
): EquipmentPoint {
  if (!degrees) return { x: p.x, y: p.y };
  const rad = (degrees * Math.PI) / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  const dx = p.x - pivot.x;
  const dy = p.y - pivot.y;
  return {
    x: pivot.x + dx * cos - dy * sin,
    y: pivot.y + dx * sin + dy * cos,
  };
}

/** Polygon vertices in world space with the instance rotation baked in. */
export function worldPolygon(instance: EquipmentInstance): EquipmentPoint[] {
  const pivot = polygonCentroid(instance.polygon);
  return instance.polygon.map((p) => rotatePoint(p, pivot, instance.rotation));
}

/** Signed-area shoelace test point-in-polygon (even-odd rule). Expects world pts. */
export function pointInPolygon(x: number, y: number, poly: EquipmentPoint[]): boolean {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const xi = poly[i].x;
    const yi = poly[i].y;
    const xj = poly[j].x;
    const yj = poly[j].y;
    const intersect = yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi + 1e-12) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
}

const MIN_FOOTPRINT_SIZE = 1;

/** True when a footprint has enough vertices and non-degenerate area to save. */
export function isValidEquipmentFootprint(polygon: EquipmentPoint[]): boolean {
  if (polygon.length < 3) return false;
  const bounds = polygonBounds(polygon);
  const width = bounds.maxX - bounds.minX;
  const height = bounds.maxY - bounds.minY;
  if (!Number.isFinite(width) || !Number.isFinite(height)) return false;
  return width > MIN_FOOTPRINT_SIZE && height > MIN_FOOTPRINT_SIZE;
}

/** Build an axis-aligned rectangle polygon from two opposite corners. */
export function rectPolygon(a: EquipmentPoint, b: EquipmentPoint): EquipmentPoint[] {
  const minX = Math.min(a.x, b.x);
  const minY = Math.min(a.y, b.y);
  const maxX = Math.max(a.x, b.x);
  const maxY = Math.max(a.y, b.y);
  return [
    { x: minX, y: minY },
    { x: maxX, y: minY },
    { x: maxX, y: maxY },
    { x: minX, y: maxY },
  ];
}

export function translatePolygon(
  polygon: EquipmentPoint[],
  dx: number,
  dy: number
): EquipmentPoint[] {
  return polygon.map((p) => ({ x: p.x + dx, y: p.y + dy }));
}

/** Scale a polygon around its centroid (used for resize). */
export function scalePolygon(
  polygon: EquipmentPoint[],
  scaleX: number,
  scaleY: number,
  pivot?: EquipmentPoint
): EquipmentPoint[] {
  const c = pivot ?? polygonCentroid(polygon);
  return polygon.map((p) => ({
    x: c.x + (p.x - c.x) * scaleX,
    y: c.y + (p.y - c.y) * scaleY,
  }));
}

/** Convert a port's relative (rx, ry) into world coordinates (with rotation). */
export function portWorldPosition(
  port: EquipmentPort,
  instance: EquipmentInstance
): EquipmentPoint {
  const b = polygonBounds(instance.polygon);
  const local = {
    x: b.minX + port.rx * (b.maxX - b.minX),
    y: b.minY + port.ry * (b.maxY - b.minY),
  };
  const pivot = polygonCentroid(instance.polygon);
  return rotatePoint(local, pivot, instance.rotation);
}

/**
 * Given a world-space point, compute the (rx, ry) of a port clamped onto the
 * nearest edge of the (unrotated) bounding box. Used while dragging a port.
 */
export function portRelativeFromWorld(
  world: EquipmentPoint,
  instance: EquipmentInstance
): { rx: number; ry: number; direction: number } {
  const pivot = polygonCentroid(instance.polygon);
  const local = rotatePoint(world, pivot, -instance.rotation);
  const b = polygonBounds(instance.polygon);
  const w = Math.max(1e-6, b.maxX - b.minX);
  const h = Math.max(1e-6, b.maxY - b.minY);
  let rx = (local.x - b.minX) / w;
  let ry = (local.y - b.minY) / h;
  rx = Math.min(1, Math.max(0, rx));
  ry = Math.min(1, Math.max(0, ry));

  // Snap onto the closest edge so ports live on the boundary.
  const distLeft = rx;
  const distRight = 1 - rx;
  const distTop = ry;
  const distBottom = 1 - ry;
  const min = Math.min(distLeft, distRight, distTop, distBottom);
  let direction: number;
  if (min === distLeft) {
    rx = 0;
    direction = 180;
  } else if (min === distRight) {
    rx = 1;
    direction = 0;
  } else if (min === distTop) {
    ry = 0;
    direction = 270;
  } else {
    ry = 1;
    direction = 90;
  }
  return { rx, ry, direction };
}

// ─── Template inheritance ────────────────────────────────────────────────────
// Instances inherit template defaults but may override individual fields. These
// resolvers are the single source of truth for "effective" values so rendering,
// simulation and analytics all agree without duplicating template data.

export function resolveTemplate(
  instance: EquipmentInstance,
  templates: Record<string, EquipmentTemplate>
): EquipmentTemplate | null {
  return instance.templateId ? (templates[instance.templateId] ?? null) : null;
}

export function resolveStyle(
  instance: EquipmentInstance,
  template: EquipmentTemplate | null
): EquipmentStyle {
  const base = template?.defaultStyle ?? defaultStyleForType(instance.objectType);
  return { ...base, ...(instance.overrides.style ?? {}) };
}

/** Full-color saved overlays vs muted DXF extract overlays. */
export type OverlayDisplayStyle = EquipmentStyle & {
  strokeOpacity: number;
  labelOpacity: number;
  labelColor: string;
  strokeWidthScale: number;
};

/** Full red for layout-saved equipment overlays. */
export const LAYOUT_EQUIPMENT_COLOR = '#0f2744';

/**
 * Visual treatment by provenance:
 * - layout (saved): full red fill + white label
 * - dxf / manual: light translucent overlay wash (category color)
 */
export function resolveOverlayDisplayStyle(
  instance: EquipmentInstance,
  template: EquipmentTemplate | null
): OverlayDisplayStyle {
  const base = resolveStyle(instance, template);
  const provenance = equipmentProvenance(instance);
  if (provenance === 'layout') {
    return {
      ...base,
      color: LAYOUT_EQUIPMENT_COLOR,
      fillOpacity: 0.85,
      strokeOpacity: 1,
      labelOpacity: 1,
      labelColor: '#ffffff',
      strokeWidthScale: 1.35,
    };
  }
  return {
    ...base,
    // Keep category hue but wash it so DXF reads as a light overlay.
    fillOpacity: Math.max(base.fillOpacity ?? 0.14, 0.28),
    strokeOpacity: 0.75,
    labelOpacity: 0.55,
    labelColor: base.color,
    strokeWidthScale: 0.85,
  };
}

export function resolveProperties(
  instance: EquipmentInstance,
  template: EquipmentTemplate | null
): EquipmentProperties {
  return { ...(template?.defaultProperties ?? {}), ...(instance.overrides.properties ?? {}) };
}

/**
 * Overlay instances live in `state.equipment`, not `state.layout.elements`.
 * Build the same rectangle + ports shape the 3D kit and OHT dock resolver use
 * so a drawn tool hands the FOUP over on the same line as a layout tool.
 */
/**
 * Unrotated top-left corner of an instance, in world space.
 *
 * An instance is drawn by rotating its footprint about the centroid, but a
 * `LayoutElement` — and the 3D group built from one — anchors its transform at
 * the unrotated top-left. For a rotated tool those corners are different, so
 * the polygon's `minX/minY` is not the origin: using it rotates the tool about
 * the wrong pivot and lands it a full footprint away from where 2D draws it.
 */
export function equipmentInstanceOrigin(instance: EquipmentInstance): EquipmentPoint {
  const polygon = instance.polygon ?? [];
  const bounds = polygonBounds(polygon);
  const centroid = polygonCentroid(polygon);
  const width = Math.max(0.5, bounds.maxX - bounds.minX);
  const height = Math.max(0.5, bounds.maxY - bounds.minY);
  const rad = ((instance.rotation || 0) * Math.PI) / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  return {
    x: centroid.x - (cos * width) / 2 + (sin * height) / 2,
    y: centroid.y - (sin * width) / 2 - (cos * height) / 2,
  };
}

export function equipmentInstanceToLayoutElement(
  instance: EquipmentInstance,
  template: EquipmentTemplate | null = null
): LayoutElement {
  const bounds = polygonBounds(instance.polygon ?? []);
  const width = Math.max(0.5, bounds.maxX - bounds.minX);
  const height = Math.max(0.5, bounds.maxY - bounds.minY);
  const origin = equipmentInstanceOrigin(instance);
  const ports = resolvePorts(instance, template);
  const properties = resolveProperties(instance, template);
  const dxfEquipmentId = instance.metadata.dxfEquipmentId;
  return {
    id: instance.id,
    layerId: 'equipment-overlay',
    type:
      instance.objectType === 'stocker'
        ? 'stocker'
        : (instance.objectType as string) === 'lift' || (instance.objectType as string) === 'lifter'
          ? 'lifter'
          : 'rectangle',
    position: { x: origin.x, y: origin.y, z: 0 },
    rotation: instance.rotation || 0,
    dimensions: { width, height },
    metadata: {
      name: instance.name?.trim() || instance.id,
      locked: false,
      visible: true,
    },
    customProperties: {
      objectType: instance.objectType,
      toolId: instance.id,
      amhsId: instance.name?.trim() || instance.id,
      loadPortId: instance.name?.trim() || instance.id,
      ports,
      ...properties,
      ...(typeof dxfEquipmentId === 'string' && dxfEquipmentId
        ? { dxfEquipmentId }
        : {}),
    },
  };
}

/** Effective ports: instance override wins, else template, else dock-edge defaults. */
export function resolvePorts(
  instance: EquipmentInstance,
  template: EquipmentTemplate | null
): EquipmentPort[] {
  const parentType = instance.objectType === 'stocker' ? 'stocker' : 'equipment';
  if (instance.overrides.ports?.length) {
    return normalizePorts(instance.id, parentType, instance.overrides.ports);
  }
  if (template?.ports.length) {
    return normalizePorts(instance.id, parentType, template.ports);
  }
  if (instance.objectType === 'equipment' || instance.objectType === 'stocker') {
    return makeDefaultPorts({
      parentId: instance.id,
      parentType,
    });
  }
  return [];
}

/** Key used to bucket equipment that has no process assigned. */
export const PROCESS_NONE_KEY = '__none__';
/** Human label for the "no process" bucket. */
export const PROCESS_NONE_LABEL = 'Unassigned';

/**
 * Process an equipment instance belongs to, derived from the backend
 * `work_cell` value at import time. Returns null when unassigned.
 */
export function equipmentProcess(inst: EquipmentInstance): string | null {
  const fromProps = inst.overrides.properties?.process;
  if (typeof fromProps === 'string' && fromProps.length > 0) return fromProps;
  const fromMeta = inst.metadata?.process;
  if (typeof fromMeta === 'string' && fromMeta.length > 0) return fromMeta;
  return null;
}

/** Bucket key for an instance's process (falls back to the "none" bucket). */
export function equipmentProcessKey(inst: EquipmentInstance): string {
  return equipmentProcess(inst) ?? PROCESS_NONE_KEY;
}

/** Whether an equipment overlay should render given process-visibility filters. */
export function isEquipmentVisibleForProcesses(
  inst: EquipmentInstance,
  hiddenProcesses: Record<string, boolean> = {}
): boolean {
  return !hiddenProcesses[equipmentProcessKey(inst)];
}

/** Whether an equipment instance is currently painted by the 2D overlay. */
export function isEquipmentOverlayVisible(
  inst: EquipmentInstance,
  options: {
    overlayVisible: boolean;
    dxfLayers: DxfLayer[];
    hiddenEntityTypes?: Record<string, boolean>;
    hiddenProcesses?: Record<string, boolean>;
    layoutElements: Record<string, LayoutElement>;
  }
): boolean {
  if (equipmentReviewStatus(inst) === 'rejected') return false;
  if (!isEquipmentVisibleForDxfLayers(inst, options.dxfLayers, options.hiddenEntityTypes)) {
    return false;
  }
  if (!isEquipmentVisibleForProcesses(inst, options.hiddenProcesses)) return false;

  if (equipmentProvenance(inst) === 'layout') {
    if (options.layoutElements[inst.id]) return false;
    return !Object.values(options.layoutElements).some(
      (element) => element?.customProperties?.dxfEquipmentId === inst.id
    );
  }

  return options.overlayVisible;
}

/** DXF layer name for imported equipment; null for manually drawn objects. */
export function equipmentDxfLayerName(inst: EquipmentInstance): string | null {
  const fromMeta = inst.metadata?.layer;
  if (typeof fromMeta === 'string' && fromMeta.length > 0) return fromMeta;
  const fromProps = inst.overrides.properties?.layer;
  if (typeof fromProps === 'string' && fromProps.length > 0) return fromProps;
  return null;
}

/** Where an overlay instance came from — used for UI / canvas differentiation. */
export type EquipmentProvenance = 'layout' | 'dxf' | 'manual';

export function equipmentProvenance(inst: EquipmentInstance): EquipmentProvenance {
  if (inst.metadata?.restoredFromLayoutElement === true) return 'layout';
  const source = inst.metadata?.source;
  if (source === 'backend-json-insert') return 'dxf';
  if (Array.isArray(inst.tags) && inst.tags.includes('insert-extraction')) return 'dxf';
  return 'manual';
}

/** Effective review state; non-DXF equipment is trusted by default. */
export function equipmentReviewStatus(inst: EquipmentInstance): EquipmentReviewStatus {
  if (
    inst.reviewStatus === 'potential' ||
    inst.reviewStatus === 'accepted' ||
    inst.reviewStatus === 'rejected'
  ) {
    return inst.reviewStatus;
  }
  return equipmentProvenance(inst) === 'dxf' ? 'potential' : 'accepted';
}

export const EQUIPMENT_PROVENANCE_LABEL: Record<EquipmentProvenance, string> = {
  layout: 'Layout save',
  dxf: 'DXF extract',
  manual: 'Manual',
};

/** Canvas accent dash for layout-restored overlays (matches pink fill). */
export const LAYOUT_EQUIPMENT_STROKE = LAYOUT_EQUIPMENT_COLOR;

/**
 * True when an overlay was generated from the imported DXF rather than drawn by
 * hand. Only these are destroyed and re-generated when layer visibility changes;
 * hand-drawn equipment is user data and must survive.
 */
export function isDxfDerivedEquipment(inst: EquipmentInstance): boolean {
  return equipmentProvenance(inst) === 'dxf';
}

/** Whether an equipment overlay should render given current DXF layer visibility. */
export function isEquipmentVisibleForDxfLayers(
  inst: EquipmentInstance,
  layers: DxfLayer[],
  hiddenEntityTypes: Record<string, boolean> = {}
): boolean {
  // Layout-restored overlays are independent of CAD layer / INSERT toggles.
  if (equipmentProvenance(inst) === 'layout') return true;

  const layerName = equipmentDxfLayerName(inst);
  if (!layerName) return true;

  const layerIndex = layers.findIndex((l) => l.name === layerName);
  if (layerIndex < 0) return true;

  if (!layers[layerIndex].visible) return false;

  if (equipmentProvenance(inst) === 'dxf' && hiddenEntityTypes[`${layerIndex}:INSERT`]) {
    return false;
  }

  return true;
}
